import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SmoothLink as Link } from "@/components/navigation";
import { Menu, X, Globe, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { useTreatmentCategories } from "@/hooks/useTreatmentCategories";
import { COMPANY, getWhatsAppLink } from "@/config/company";
import { NAV_LINKS, CTA_LINKS } from "@/config/navigation";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.scrollY > 50;
    }
    return false;
  });
  const location = useLocation();

  const {
    data: categories = [],
    isLoading: categoriesLoading
  } = useTreatmentCategories();
  const { trackWhatsAppClick, trackCTAClick } = useAnalyticsContext();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      { 
        threshold: [0]
      }
    );

    const sentinel = document.getElementById("scroll-sentinel");
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => observer.disconnect();
  }, []);

  const isHome = location.pathname === "/";
  const navLinkClass = (isActive: boolean) => 
    cn(
      "group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors focus:outline-none",
      isScrolled || !isHome 
        ? "text-foreground hover:text-primary" 
        : "text-secondary-foreground hover:text-accent",
      isActive && "text-primary font-semibold"
    );

  const prefetchRoute = (href: string) => {
    switch (href) {
      case "/hospitals/":
        import("../../pages/Hospitals");
        break;
      case "/doctors/":
        import("../../pages/Doctors");
        break;
      case "/blog/":
        import("../../pages/Blog");
        break;
      case "/contact/":
        import("../../pages/Contact");
        break;
    }
  };

  return (
    <header className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b", isScrolled || !isHome ? "bg-background/95 backdrop-blur-md border-border/50 shadow-soft" : "bg-transparent border-transparent shadow-none")}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              width={188}
              height={40}
              src={isScrolled || !isHome ? "/logos/logo-full-color.svg" : "/logos/logo-full-white.svg"} 
              alt="TrustedHeal" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* HYDRATION FIX: Use standard Links for simple items to reduce weight */}
            {NAV_LINKS.map((link) => {
              if (link.title === "Treatments") {
                 // Keep Dropdown for Treatments
                 return (
                   <NavigationMenu key={link.title}>
                    <NavigationMenuList>
                      <NavigationMenuItem>
                        <NavigationMenuTrigger 
                          className={cn("bg-transparent font-medium", isScrolled || !isHome ? "text-foreground hover:text-primary" : "text-secondary-foreground hover:text-accent")}
                          onMouseEnter={() => import("../../pages/Treatments")}
                        >
                          Treatments
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid w-[90vw] sm:w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] lg:grid-cols-3 bg-background">
                            {/* View All Link - Top for better reach */}
                            {!categoriesLoading && <li className="col-span-full border-b border-border pb-3 mb-2">
                                <NavigationMenuLink asChild>
                                  <Link 
                                    to="/treatments/" 
                                    className="flex items-center justify-center gap-2 text-sm font-medium text-primary hover:underline py-1"
                                    onMouseEnter={() => import("../../pages/Treatments")}
                                  >
                                    View All Treatments →
                                  </Link>
                                </NavigationMenuLink>
                              </li>}
                            {categoriesLoading ? (
                              <li className="col-span-full flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                              </li>
                            ) : (
                              categories.map(category => (
                                <li key={category.id}>
                                  <NavigationMenuLink asChild>
                                    <Link to={category.href} className="block select-none space-y-1 rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-primary focus:bg-accent/10">
                                      <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium leading-none text-secondary">{category.title}</span>
                                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                          {category.treatmentCount}
                                        </span>
                                      </div>
                                      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                                        {category.description}
                                      </p>
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))
                            )}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                 );
              }
              
              // Standard Links
              return (
                <Link 
                  key={link.title} 
                  to={link.href} 
                  className={navLinkClass(location.pathname === link.href)}
                  onMouseEnter={() => prefetchRoute(link.href)}
                >
                  {link.title}
                </Link>
              );
            })}
          </nav>

          {/* Right Side CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" size="sm" className={cn("gap-2 rounded-full", isScrolled || !isHome ? "text-foreground" : "text-secondary-foreground border-secondary-foreground/30")}>
              <Globe className="w-4 h-4" />
              EN
            </Button>
            <Button variant="ghost" size="sm" className={cn("rounded-full", isScrolled || !isHome ? "text-foreground hover:bg-muted" : "text-secondary-foreground hover:bg-secondary-foreground/10")}>
              Login
            </Button>
            <Button asChild variant="default" className="rounded-full px-6 font-medium">
              <Link 
                to={CTA_LINKS.quote.href}
                onClick={() => trackCTAClick('Get Free Quote', 'navbar', 'primary')}
              >
                {CTA_LINKS.quote.title}
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className={cn("lg:hidden p-2", isScrolled || !isHome ? "text-foreground" : "text-secondary-foreground")} onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-slide-up bg-background">
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map(link => {
                  if (link.title === "Treatments") {
                      return (
                          <div key="mobile-treatments-section">
                              <div className="px-4 py-2">
                                <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Treatments</span>
                              </div>
                              {categories.slice(0, 6).map(category => (
                                <Link 
                                    key={category.id} 
                                    to={category.href} 
                                    className="px-6 py-2 text-foreground hover:text-primary rounded-lg hover:bg-muted flex items-center justify-between" 
                                    onClick={() => setIsOpen(false)}
                                >
                                  <span>{category.title}</span>
                                  <span className="text-xs text-muted-foreground">{category.treatmentCount}</span>
                                </Link>
                              ))}
                              <Link to="/treatments/" className="px-6 py-2 text-primary font-medium rounded-lg hover:bg-muted" onClick={() => setIsOpen(false)}>
                                View All Treatments →
                              </Link>
                          </div>
                      );
                  }
                  return (
                    <Link 
                        key={link.title} 
                        to={link.href} 
                        className="px-4 py-3 text-foreground hover:text-primary font-medium rounded-lg hover:bg-muted" 
                        onClick={() => setIsOpen(false)}
                    >
                      {link.title}
                    </Link>
                  );
              })}
              
              <div className="pt-4 mt-2 border-t border-border space-y-2">
                <Button asChild variant="accent" className="w-full rounded-full font-medium gap-2">
                  <a 
                    href={getWhatsAppLink()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackWhatsAppClick('mobile_nav')}
                  >
                    <MessageCircle className="w-4 h-4" />
                    {CTA_LINKS.whatsapp.title}
                  </a>
                </Button>
                <Button asChild variant="default" className="w-full rounded-full font-medium">
                  <Link 
                    to={CTA_LINKS.quote.href} 
                    onClick={() => {
                      trackCTAClick('Get Free Quote', 'mobile_nav', 'primary');
                      setIsOpen(false);
                    }}
                  >
                    {CTA_LINKS.quote.title}
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}